import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DollarSign, TrendingUp, Plus, Trash2, ChevronRight, ChevronLeft, Zap, X, Check, Pencil,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip,
} from 'recharts';
import api from '../../services/api';
import Button from './Button';
import type { Settings } from '../../types';

interface IncomeChange {
  id: string;
  month: number; // 1-12
  year: number;
  amount: number; // durable : montant absolu · ponctuel : delta signé
  isOneOff: boolean;
  label: string | null;
}

interface Props {
  /** Nom affiché du partenaire (prénom si connu, sinon terme générique). */
  partnerName: string;
  /** Revenu de base (avant le 1ᵉʳ durable), issu des settings du couple. */
  baseIncome: number | null;
  dateLocale: string;
  /** Appelé après sauvegarde du revenu de base pour rafraîchir le store. */
  onSettingsUpdated: (settings: Settings) => void;
  onMessage: (msg: { type: 'success' | 'error'; text: string }) => void;
}

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const now = new Date();
const NOW = { month: now.getMonth() + 1, year: now.getFullYear() };

export default function PartnerIncomeCard({
  partnerName, baseIncome, dateLocale, onSettingsUpdated, onMessage,
}: Props) {
  const { t } = useTranslation('profile');
  const partnerNameLower = partnerName.toLowerCase();

  const [changes, setChanges] = useState<IncomeChange[]>([]);
  const [openYears, setOpenYears] = useState<Record<number, boolean>>({}); // tout replié au départ
  const [chartYear, setChartYear] = useState<number>(NOW.year);

  // Panneau d'ajout
  const [composeOpen, setComposeOpen] = useState(false);
  const [mode, setMode] = useState<'durable' | 'once'>('durable');
  const [fMonth, setFMonth] = useState<number>(NOW.month);
  const [fYear, setFYear] = useState<number>(NOW.year);
  const [fAmount, setFAmount] = useState<string>('');
  const [fLabel, setFLabel] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  // Édition inline du revenu de base
  const [editingBase, setEditingBase] = useState(false);
  const [baseDraft, setBaseDraft] = useState<string>('');
  const [isSavingBase, setIsSavingBase] = useState(false);

  const fmt = useCallback(
    (n: number) => new Intl.NumberFormat(dateLocale).format(Math.round(n)),
    [dateLocale],
  );
  const monthLong = useCallback(
    (m: number) => new Date(2025, m - 1, 1).toLocaleDateString(dateLocale, { month: 'long' }),
    [dateLocale],
  );
  const monthShort = useCallback(
    (m: number) => new Date(2025, m - 1, 1).toLocaleDateString(dateLocale, { month: 'short' }),
    [dateLocale],
  );

  const base = baseIncome ?? 0;
  const key = (m: number, y: number) => y * 12 + (m - 1);

  const fetchChanges = useCallback(async () => {
    try {
      const res = await api.get('/settings/partner2-income');
      if (res.data.success) setChanges(res.data.data.changes as IncomeChange[]);
    } catch {
      // échec silencieux : la carte reste utilisable
    }
  }, []);

  useEffect(() => { fetchChanges(); }, [fetchChanges]);

  // --- Résolution du revenu ---------------------------------------------------
  // Niveau durable en vigueur à (m, y) : dernier durable daté ≤ (m, y), sinon revenu de base.
  const durableAt = useCallback((m: number, y: number): { amount: number; label: string | null; m: number | null; y: number | null } => {
    let best: IncomeChange | null = null;
    for (const c of changes) {
      if (c.isOneOff) continue;
      if (key(c.month, c.year) <= key(m, y) && (!best || key(c.month, c.year) > key(best.month, best.year))) {
        best = c;
      }
    }
    return best
      ? { amount: best.amount, label: best.label, m: best.month, y: best.year }
      : { amount: base, label: null, m: null, y: null };
  }, [changes, base]);

  // Revenu résolu = niveau durable + somme des ponctuels datés sur ce mois précis.
  const resolveAt = useCallback((m: number, y: number) => {
    const d = durableAt(m, y);
    const extra = changes
      .filter((c) => c.isOneOff && c.month === m && c.year === y)
      .reduce((s, c) => s + c.amount, 0);
    return { amount: d.amount + extra, level: d.amount, extra };
  }, [durableAt, changes]);

  // Niveau durable en vigueur juste avant la date d'effet d'un durable (pour le delta affiché).
  const durableBeforeAmount = useCallback((c: IncomeChange): number => {
    const pm = c.month === 1 ? 12 : c.month - 1;
    const py = c.month === 1 ? c.year - 1 : c.year;
    return durableAt(pm, py).amount;
  }, [durableAt]);

  // --- Données dérivées -------------------------------------------------------
  const dataYears = useMemo(() => {
    const set = new Set<number>(changes.map((c) => c.year));
    set.add(NOW.year);
    return Array.from(set).sort((a, b) => b - a);
  }, [changes]);

  const changesByYear = useMemo(() => {
    const map = new Map<number, IncomeChange[]>();
    for (const c of changes) {
      const arr = map.get(c.year) ?? [];
      arr.push(c);
      map.set(c.year, arr);
    }
    return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
  }, [changes]);

  const chartData = useMemo(() => MONTHS.map((m) => {
    const r = resolveAt(m, chartYear);
    return {
      label: monthShort(m),
      amount: r.amount,
      isOnce: r.extra !== 0,
      isCurrent: chartYear === NOW.year && m === NOW.month,
    };
  }), [resolveAt, chartYear, monthShort]);

  // Revenu en vigueur ce mois-ci (pour le hero)
  const heroDurable = durableAt(NOW.month, NOW.year);
  const heroOnce = changes
    .filter((c) => c.isOneOff && c.month === NOW.month && c.year === NOW.year)
    .reduce((s, c) => s + c.amount, 0);

  // --- Handlers ---------------------------------------------------------------
  const toggleYear = (year: number) => {
    setOpenYears((prev) => ({ ...prev, [year]: !prev[year] }));
    setChartYear(year); // le graphe suit l'année sur laquelle on interagit
  };

  const stepChartYear = (dir: -1 | 1) => {
    const i = dataYears.indexOf(chartYear);
    // dataYears est trié décroissant : +1 (année suivante) = index plus petit
    const next = dir === 1 ? dataYears[i - 1] : dataYears[i + 1];
    if (next !== undefined) setChartYear(next);
  };

  const switchMode = (m: 'durable' | 'once') => {
    setMode(m);
    setFAmount('');
  };

  const handleAdd = async () => {
    const amount = parseFloat(fAmount.replace(',', '.'));
    const isOneOff = mode === 'once';
    if (isNaN(amount) || (isOneOff && amount === 0) || (!isOneOff && amount < 0)) {
      onMessage({ type: 'error', text: t('couple.incomeInvalidAmount') });
      return;
    }
    setIsSaving(true);
    try {
      await api.put('/settings/partner2-income', {
        month: fMonth,
        year: fYear,
        amount,
        isOneOff,
        label: fLabel.trim() || null,
      });
      await fetchChanges();
      setOpenYears((prev) => ({ ...prev, [fYear]: true }));
      setChartYear(fYear);
      setFAmount('');
      setFLabel('');
      setComposeOpen(false);
      onMessage({ type: 'success', text: t('couple.incomeChangeSaved') });
    } catch {
      onMessage({ type: 'error', text: t('updateError', { ns: 'common' }) });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/settings/partner2-income/${id}`);
      await fetchChanges();
    } catch {
      onMessage({ type: 'error', text: t('updateError', { ns: 'common' }) });
    }
  };

  const startEditBase = () => {
    setBaseDraft(baseIncome != null ? String(baseIncome) : '');
    setEditingBase(true);
  };

  const handleSaveBase = async () => {
    const value = baseDraft.trim() ? parseFloat(baseDraft.replace(',', '.')) : null;
    if (value !== null && (isNaN(value) || value < 0)) {
      onMessage({ type: 'error', text: t('couple.incomeInvalidAmount') });
      return;
    }
    setIsSavingBase(true);
    try {
      const res = await api.put('/settings', { partner2ManualMonthlyIncome: value });
      if (res.data.success) {
        onSettingsUpdated(res.data.data);
        setEditingBase(false);
        onMessage({ type: 'success', text: t('couple.manualIncomeSaved') });
      }
    } catch {
      onMessage({ type: 'error', text: t('updateError', { ns: 'common' }) });
    } finally {
      setIsSavingBase(false);
    }
  };

  // --- Rendu ------------------------------------------------------------------
  const canPrev = dataYears.indexOf(chartYear) < dataYears.length - 1;
  const canNext = dataYears.indexOf(chartYear) > 0;

  return (
    <div className="card border-amber-200 bg-amber-50/60">
      {/* En-tête */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 grid place-items-center shrink-0">
          <DollarSign className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900 leading-tight truncate">
            {t('couple.manualIncomeTitle', { partner: partnerNameLower })}
          </h2>
          <p className="text-xs text-gray-500">{t('couple.incomeCardSubtitle')}</p>
        </div>
      </div>

      {/* Hero + graphe */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        {/* Revenu en vigueur */}
        <div className="flex flex-col justify-center gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-700/80">
            {t('couple.incomeInForce')}
          </span>
          {editingBase ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={baseDraft}
                onChange={(e) => setBaseDraft(e.target.value)}
                placeholder={t('couple.manualIncomePlaceholder')}
                min="0"
                step="0.01"
                autoFocus
                className="w-32 px-3 py-2 border border-amber-300 rounded-lg bg-white text-lg font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-300"
              />
              <button
                type="button"
                onClick={handleSaveBase}
                disabled={isSavingBase}
                className="p-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                aria-label={t('personalInfo.save')}
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setEditingBase(false)}
                className="p-2 rounded-lg text-gray-400 hover:bg-gray-100"
                aria-label={t('cancel', { ns: 'common' })}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-baseline gap-1.5">
                <span className="text-4xl font-bold tracking-tight text-gray-900 tabular-nums">
                  {fmt(heroDurable.amount + heroOnce)}
                </span>
                <span className="text-sm font-semibold text-gray-400">{t('couple.incomeMonthUnit')}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap text-xs text-gray-600">
                <span>
                  {heroDurable.m
                    ? t('couple.incomeSinceDate', { month: monthLong(heroDurable.m), year: heroDurable.y })
                    : t('couple.incomeSinceStart')}
                </span>
                {heroDurable.label && heroDurable.m && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">{heroDurable.label}</span>
                )}
                {heroOnce !== 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300 font-semibold">
                    <Zap className="w-3 h-3" />
                    {t('couple.incomeThisMonthChip', { sign: heroOnce < 0 ? '−' : '+', amount: fmt(Math.abs(heroOnce)) })}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={startEditBase}
                className="self-start inline-flex items-center gap-1 mt-1 text-xs font-medium text-amber-700 hover:underline"
              >
                <Pencil className="w-3 h-3" /> {t('couple.incomeEditBase')}
              </button>
            </>
          )}
        </div>

        {/* Graphe en escalier */}
        <div className="bg-white border border-amber-100 rounded-xl p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-amber-500" /> {t('couple.incomeChartTitle')}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => stepChartYear(-1)}
                disabled={!canPrev}
                className="w-6 h-6 grid place-items-center rounded text-gray-400 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-30 disabled:hover:bg-transparent"
                aria-label={t('couple.incomeFieldYear')}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-bold tabular-nums w-11 text-center">{chartYear}</span>
              <button
                type="button"
                onClick={() => stepChartYear(1)}
                disabled={!canNext}
                className="w-6 h-6 grid place-items-center rounded text-gray-400 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-30 disabled:hover:bg-transparent"
                aria-label={t('couple.incomeFieldYear')}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={132}>
            <AreaChart data={chartData} margin={{ top: 6, right: 4, bottom: 0, left: -18 }}>
              <defs>
                <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1e9d8" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval={0} />
              <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => fmt(v)} />
              <Tooltip
                formatter={(v: number) => [`${fmt(v)} €`, t('couple.incomeInForce')]}
                labelStyle={{ color: '#6b7280', fontSize: 12 }}
                contentStyle={{ borderRadius: 8, border: '1px solid #fde68a', fontSize: 12 }}
              />
              <Area
                type="stepAfter"
                dataKey="amount"
                stroke="#d97706"
                strokeWidth={2}
                fill="url(#incomeGrad)"
                dot={(props: { cx?: number; cy?: number; index?: number; payload?: { isOnce?: boolean; isCurrent?: boolean } }) => {
                  const { cx, cy, index, payload } = props;
                  const k = `dot-${index ?? 0}`;
                  if (cx == null || cy == null) return <g key={k} />;
                  if (payload?.isOnce) {
                    return <circle key={k} cx={cx} cy={cy} r={3.4} fill="#c2740c" stroke="#fff" strokeWidth={1.5} />;
                  }
                  if (payload?.isCurrent) {
                    return <circle key={k} cx={cx} cy={cy} r={3} fill="#1c1917" />;
                  }
                  return <g key={k} />;
                }}
                activeDot={{ r: 4, fill: '#d97706' }}
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex gap-3 mt-1 text-[10px] text-gray-400">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block bg-amber-500" />{t('couple.incomeLegendDurable')}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block bg-amber-700" />{t('couple.incomeLegendOnce')}</span>
          </div>
        </div>
      </div>

      {/* Historique */}
      <div className="border-t border-amber-200 pt-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{t('couple.incomeHistoryTitle')}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{t('couple.incomeHistoryHint')}</p>
          </div>
          <button
            type="button"
            onClick={() => setComposeOpen((o) => !o)}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-300 bg-amber-100 text-amber-800 text-sm font-semibold hover:bg-amber-200 transition-colors"
          >
            {composeOpen ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {composeOpen ? t('couple.incomeCloseButton') : t('couple.incomeAddButton')}
          </button>
        </div>

        {/* Panneau d'ajout */}
        {composeOpen && (
          <div className="bg-white border border-amber-100 rounded-xl p-4 mb-3">
            <div className="inline-flex bg-amber-50 border border-amber-200 rounded-lg p-0.5 mb-3">
              <button
                type="button"
                onClick={() => switchMode('durable')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${mode === 'durable' ? 'bg-amber-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {t('couple.incomeModeDurable')}
              </button>
              <button
                type="button"
                onClick={() => switchMode('once')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-1 transition-colors ${mode === 'once' ? 'bg-amber-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Zap className="w-3.5 h-3.5" /> {t('couple.incomeModeOncePonctuel')}
              </button>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{t('couple.incomeFieldFrom')}</label>
                <select
                  value={fMonth}
                  onChange={(e) => setFMonth(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm capitalize focus:outline-none focus:ring-2 focus:ring-amber-300"
                >
                  {MONTHS.map((m) => (
                    <option key={m} value={m} className="capitalize">{monthLong(m)}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{t('couple.incomeFieldYear')}</label>
                <input
                  type="number"
                  value={fYear}
                  onChange={(e) => setFYear(Number(e.target.value))}
                  className="w-20 px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                  {mode === 'once' ? t('couple.incomeFieldExtra') : t('couple.incomeFieldNewIncome')}
                </label>
                <input
                  type="number"
                  value={fAmount}
                  onChange={(e) => setFAmount(e.target.value)}
                  placeholder={mode === 'once' ? t('couple.incomeExtraPlaceholder') : t('couple.manualIncomePlaceholder')}
                  step="10"
                  className="w-28 px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
                <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{t('couple.incomeFieldNote')}</label>
                <input
                  type="text"
                  value={fLabel}
                  onChange={(e) => setFLabel(e.target.value)}
                  placeholder={t('couple.incomeNotePlaceholder')}
                  maxLength={100}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
              {mode === 'once' ? (
                <span className="text-xs text-amber-700 flex items-center gap-1.5">
                  <ChevronRight className="w-3 h-3" /> {t('couple.incomeOnceHintNew')}
                </span>
              ) : <span />}
              <Button variant="primary" size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={handleAdd} isLoading={isSaving}>
                {t('couple.incomeAdd')}
              </Button>
            </div>
          </div>
        )}

        {/* Timeline scrollable regroupée par année */}
        <div className="relative">
          <div className="max-h-80 overflow-y-auto pr-1">
            {changesByYear.length === 0 && (
              <p className="text-sm text-gray-400 italic text-center py-4">{t('couple.incomeNoChangesYet')}</p>
            )}

            {changesByYear.map(([year, list]) => {
              const open = !!openYears[year];
              const monthlyTotals = list.map((c) => resolveAt(c.month, year).amount);
              const min = Math.min(...monthlyTotals);
              const max = Math.max(...monthlyTotals);
              return (
                <div key={year}>
                  <button
                    type="button"
                    onClick={() => toggleYear(year)}
                    className={`w-full flex items-center gap-2.5 px-2 py-2.5 rounded-lg text-left transition-colors ${open ? 'bg-amber-100/70' : 'hover:bg-amber-50'}`}
                  >
                    <ChevronRight className={`w-3 h-3 text-amber-600 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
                    <span className={`font-bold text-sm tabular-nums w-11 ${open ? 'text-amber-800' : 'text-gray-900'}`}>{year}</span>
                    <span className="flex-1 text-xs text-gray-500 truncate">
                      {min === max
                        ? t('couple.incomeYearSingle', { amount: fmt(min) })
                        : t('couple.incomeYearRange', { min: fmt(min), max: fmt(max) })}
                    </span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${open ? 'bg-amber-200 border-amber-300 text-amber-800' : 'bg-white border-amber-200 text-gray-500'}`}>
                      {t('couple.incomeYearChangeCount', { count: list.length })}
                    </span>
                  </button>

                  {open && (
                    <div className="pb-1">
                      {[...list].sort((a, b) => key(b.month, b.year) - key(a.month, a.year)).map((c) => {
                        const level = durableAt(c.month, c.year).amount;
                        if (c.isOneOff) {
                          const total = level + c.amount;
                          const sign = c.amount < 0 ? '− ' : '+ ';
                          return (
                            <div key={c.id} className="grid grid-cols-[70px_auto_1fr_auto] items-center gap-2.5 pl-5 pr-2 py-2 rounded-lg hover:bg-amber-50/70 group">
                              <span className="text-xs font-semibold text-gray-600 capitalize">{monthShort(c.month)}</span>
                              <span className="text-sm font-bold text-gray-900 tabular-nums">
                                {fmt(total)} €
                                <span className="block text-[10px] font-semibold text-gray-400">
                                  = {fmt(level)} <span className="text-amber-700">{sign}{fmt(Math.abs(c.amount))}</span>
                                </span>
                              </span>
                              <span className="flex items-center gap-2 min-w-0">
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                  <Zap className="w-3 h-3" /> {t('couple.incomeOnceBadge')}
                                </span>
                                {c.label && <span className="text-xs text-gray-500 truncate">{c.label}</span>}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleDelete(c.id)}
                                className="w-7 h-7 grid place-items-center rounded-lg text-gray-300 hover:bg-red-50 hover:text-red-500 group-hover:text-gray-400"
                                aria-label={t('delete', { ns: 'common' })}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        }
                        const delta = c.amount - durableBeforeAmount(c);
                        return (
                          <div key={c.id} className="grid grid-cols-[70px_auto_1fr_auto] items-center gap-2.5 pl-5 pr-2 py-2 rounded-lg hover:bg-amber-50/70 group">
                            <span className="text-xs font-semibold text-gray-600 capitalize">{monthShort(c.month)}</span>
                            <span className="text-sm font-bold text-gray-900 tabular-nums">{fmt(c.amount)} €</span>
                            <span className="flex items-center gap-2 min-w-0">
                              {delta !== 0 && (
                                <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${delta > 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-600 bg-red-50'}`}>
                                  {delta > 0 ? '+' : '−'}{fmt(Math.abs(delta))} €
                                </span>
                              )}
                              {c.label && <span className="text-xs text-gray-500 truncate">{c.label}</span>}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleDelete(c.id)}
                              className="w-7 h-7 grid place-items-center rounded-lg text-gray-300 hover:bg-red-50 hover:text-red-500 group-hover:text-gray-400"
                              aria-label={t('delete', { ns: 'common' })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Ligne du revenu de base (plancher) */}
            <div className="grid grid-cols-[70px_auto_1fr_auto] items-center gap-2.5 pl-5 pr-2 py-2.5 mt-1 border-t border-dashed border-amber-200">
              <span className="text-xs font-semibold text-gray-500">{t('couple.incomeBaseRowLabel')}</span>
              <span className="text-sm font-semibold text-gray-500 tabular-nums">{fmt(base)} €</span>
              <span className="text-[10px] text-gray-400 border border-dashed border-amber-300 rounded-full px-2 py-0.5 justify-self-start">
                {t('couple.incomeBaseRowBadge')}
              </span>
              <span />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
